import { Div, IRouteEvent, router } from '@codesuma/baseline'
import { Page } from '../../components/page'
import listsService from '../../services/lists'
import { iconKeyForName } from '../../components/list-circle/icon-for-name'
import { Slides, WizardNav } from './wizard'
import { NameStep } from './name-step'
import { IconPicker } from '../../components/icon-picker'
import style from './index.module.css'
import { BackIcon } from 'components/back-icon'

// New List: a two-step wizard. Step 1 names the list and can create it right
// there ("Save"); step 2 picks its icon. Skipping step 2 stores no icon,
// so the circle keeps guessing one from the name and follows a rename.
export const AddPage = () => {
    // Page() gives the shared slide transition (enter from bottom, exit up;
    // reversed on back). Override its background with the themed page tokens (these
    // are CSS vars, so the page follows light/dark live — even while cached).
    const base = Page()
    base.style({ background: 'var(--page-bg)', color: 'var(--page-fg)' })

    // Header
    const header = Div()
    header.addClass(style.header)

    const back = BackIcon('currentColor') // currentColor so it picks up the dark page foreground.
    back.el.onclick = () => router.back()

    const title = Div('New List')
    title.addClass(style.title)

    header.append(back, title)

    const stage = Slides()
    const nav = WizardNav()
    base.append(header, stage, nav)

    // The draft. `picked` stays null until an icon is tapped, so the icon step
    // re-guesses from the name if the user goes back and changes it.
    let name = ''
    let picked: string | null = null
    let step: 'name' | 'icon' = 'name'

    const renderNav = () => {
        const hasName = !!name.trim()
        nav.setState(step === 'name'
            ? { steps: 2, index: 0, nextLabel: 'Next: Pick icon', nextEnabled: hasName, showPrev: false, showDone: true }
            : { steps: 2, index: 1, nextLabel: 'Create list', nextEnabled: true, showPrev: true, showDone: false })
    }

    // Every step is built fresh: baseline's remove() strips a component's
    // children for good, so a step can't be parked and shown again.
    const goto = (next: 'name' | 'icon', direction: 'forward' | 'back' | 'none') => {
        step = next
        if (next === 'name') {
            const slide = NameStep(name)
            slide.on('text', (value: string) => {
                name = value
                renderNav()
            })
            slide.on('enter', advance)
            stage.show(slide, direction)
            // After the slide has settled, so the keyboard doesn't open onto a
            // moving field.
            setTimeout(() => slide.focus(), 220)
        } else {
            const slide = IconPicker(picked ?? iconKeyForName(name))
            slide.on('pick', (key: string) => { picked = key })
            stage.show(slide, direction)
        }
        renderNav()
    }

    // No auth gate: a brand-new (session-less) user can create their first
    // list here. createList lazily creates the anonymous session on sync.
    let creating = false
    const create = async () => {
        const value = name.trim()
        if (!value || creating) return
        creating = true
        try {
            const created = await listsService.createList(value, picked ?? undefined)
            router.goto('/list/' + created.id, { name: created.name })
        } catch (err) {
            console.error('Failed to create list:', err)
        } finally {
            creating = false
        }
    }

    const advance = () => {
        if (!name.trim()) return
        if (step === 'name') goto('icon', 'forward')
        else create()
    }

    nav.on('next', advance)
    nav.on('done', create)
    nav.on('prev', () => goto('name', 'back'))

    // Every visit starts over — the router keeps this page alive between visits.
    base.on('enter', (_: IRouteEvent) => {
        name = ''
        picked = null
        goto('name', 'none')
    })

    return base
}
